/**
 * Backoffice API client.
 *
 * No JWT or refresh token here — Cloudflare Access intercepts every request
 * to the backoffice domain and signs the upstream call with a JWT. The
 * browser carries the CF_Authorization cookie automatically, so all we need
 * is `credentials: 'include'`.
 */
const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  json?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

const buildQuery = (q?: RequestOptions['query']): string => {
  if (!q) return '';
  const parts: string[] = [];
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined) continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
};

export const api = {
  request: async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
    const url = `${baseUrl}${path}${buildQuery(options.query)}`;
    const headers = new Headers();
    // Local / Playwright only. Prod uses Cloudflare Access; never set this on Pages.
    const devAdminEmail = import.meta.env.VITE_DEV_ADMIN_EMAIL as string | undefined;
    if (devAdminEmail) headers.set('X-Dev-Admin-Email', devAdminEmail);
    let body: string | undefined;
    if (options.json !== undefined) {
      headers.set('content-type', 'application/json');
      body = JSON.stringify(options.json);
    }
    const res = await fetch(url, {
      method: options.method ?? 'GET',
      headers,
      body,
      // Dev header cannot be sent as a credentialed CORS request (API
      // `credentials: false`). Prod keeps the CF Access cookie.
      credentials: devAdminEmail ? 'omit' : 'include',
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let code: string | undefined;
      let message = text || `HTTP ${res.status}`;
      try {
        const parsed = JSON.parse(text);
        message = parsed.error?.message ?? message;
        code = parsed.error?.code;
      } catch {
        // not JSON — use raw text
      }
      throw new ApiError(message, res.status, code);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  },
};
